import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Layout from '../components/Layout.js';
import ImageUpload from '../components/ImageUpload.js';
import ColorSelector from '../components/ColorSelector.js';
import { CARD_FONTS } from '../utils/cardDesign.js';
import LockedOption from '../components/LockedOption.js';
import CardDisplay from '../components/CardDisplay.js';
import SortableLinkComponent, { LINK_ICONS, ICON_COMPONENT } from '../components/SortableLink.js';
import PhoneMockComponent from '../components/PhoneMock.js';
import { Section as FormSection, Field as FormField, FieldArea as FormFieldArea, FieldSelect as FormFieldSelect, FieldToggle as FormFieldToggle } from '../components/index.js';
import { useAuth } from '../contexts/AuthContext.js';
import { useAppTranslation } from '../hooks/useAppTranslation.js';
import {
  type CardData,
  type AppSettings,
  getDefaultTemplate,
  mergeCardDataWithDefaults,
} from '../utils/cardUtils.js';

import {
  ArrowLeft, Check, RefreshCw, Plus, X, GripVertical, Globe, Mail, Phone, MapPin,
  Briefcase, User, Link as LinkIcon, Download, FileText, Calendar, Video, Music,
  ShoppingCart, Youtube, Facebook, MessageCircle, Linkedin, Twitter, Instagram, Github,
  Eye, Sun, Moon,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TABS = [
  { id: 'details' as const, label: 'Details' },
  { id: 'links'   as const, label: 'Links'   },
  { id: 'images'  as const, label: 'Bilder'  },
  { id: 'style'   as const, label: 'Style'   },
  { id: 'privacy' as const, label: 'Privatsphäre' },
];
type TabId = 'details' | 'links' | 'images' | 'style' | 'privacy';

// Re-exported from SortableLink for local use
// COUNTRIES is kept locally as it has German labels
const COUNTRIES = [
  { value: 'DE', label: 'Deutschland'          },
  { value: 'AT', label: 'Österreich'           },
  { value: 'CH', label: 'Schweiz'              },
  { value: 'NL', label: 'Niederlande'          },
  { value: 'BE', label: 'Belgien'              },
  { value: 'LU', label: 'Luxemburg'            },
  { value: 'FR', label: 'Frankreich'           },
  { value: 'ES', label: 'Spanien'              },
  { value: 'IT', label: 'Italien'              },
  { value: 'PL', label: 'Polen'                },
  { value: 'CZ', label: 'Tschechien'           },
  { value: 'DK', label: 'Dänemark'             },
  { value: 'SE', label: 'Schweden'             },
  { value: 'NO', label: 'Norwegen'             },
  { value: 'FI', label: 'Finnland'             },
  { value: 'GB', label: 'Vereinigtes Königreich'},
  { value: 'IE', label: 'Irland'               },
  { value: 'US', label: 'USA'                  },
  { value: 'CA', label: 'Kanada'               },
  { value: 'AU', label: 'Australien'           },
];

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface OrgSettings {
  allow_theme_customisation?: boolean;
  allow_image_customisation?: boolean;
  allow_links_customisation?: boolean;
  allow_privacy_customisation?: boolean;
  default_organisation?: string;
  default_card_color?: string;
}

interface CustomLink {
  id: string;
  title: string;
  url: string;
  icon: string;
}

interface PickableUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
}

// ---------------------------------------------------------------------------
// Form primitives (use imported from components/ui/FormPrimitive)
// ---------------------------------------------------------------------------

/** Dark glass text field */
function Field({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  icon: Icon,
  disabled,
  maxLength,
  autoComplete,
  name,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  icon?: LucideIcon;
  disabled?: boolean;
  maxLength?: number;
  autoComplete?: string;
  name?: string;
}) {
  return (
    <div className="space-y-1.5">
      <label className="block text-xs font-medium text-ink-muted">{label}</label>
      <div className="relative">
        {Icon && (
          <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink-faint pointer-events-none" />
        )}
        <input
          type={type}
          name={name}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={maxLength}
          autoComplete={autoComplete ?? 'off'}
          className={`w-full rounded-xl bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white/90 text-sm
                      placeholder:text-slate-400 dark:placeholder:text-white/20 focus:outline-none focus:border-accent-500/50
                      focus:ring-1 focus:ring-accent-500/20 transition-all py-2.5
                      disabled:opacity-50 disabled:cursor-not-allowed
                      ${Icon ? 'pl-9 pr-3' : 'px-3'}`}
        />
      </div>
    </div>
  );
}

/** Dark glass textarea */
function FieldArea({
  label,
  value,
  onChange,
  rows = 3,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  placeholder?: string;
}) {
  return (
    <div className="space-y-1.5">
      <label className="block text-xs font-medium text-ink-muted">{label}</label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        className="w-full rounded-xl bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white/90 text-sm
                   placeholder:text-slate-400 dark:placeholder:text-white/20 focus:outline-none focus:border-accent-500/50
                   focus:ring-1 focus:ring-accent-500/20 transition-all px-3 py-2.5 resize-none"
      />
    </div>
  );
}

/** Dark glass select */
function FieldSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div className="space-y-1.5">
      <label className="block text-xs font-medium text-ink-muted">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white/90 text-sm
                   focus:outline-none focus:border-accent-500/50 focus:ring-1 focus:ring-accent-500/20
                   transition-all px-3 py-2.5 appearance-none cursor-pointer
                   [&>option]:bg-white dark:[&>option]:bg-panel [&>option]:text-slate-900 dark:[&>option]:text-white"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

/** Dark glass toggle */
function FieldToggle({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-4 cursor-pointer group">
      <div className="relative flex-shrink-0 mt-0.5">
        <input
          type="checkbox"
          className="sr-only"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
        />
        <div
          className={`w-11 h-6 rounded-full transition-all duration-200 ${
            checked
              ? 'bg-accent-600'
              : 'bg-raised group-hover:bg-slate-300 dark:group-hover:bg-white/15'
          }`}
        >
          <div
            className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-200 ${
              checked ? 'translate-x-5' : 'translate-x-0.5'
            }`}
          />
        </div>
      </div>
      <div className="min-w-0">
        <span className="block text-sm font-medium text-ink">{label}</span>
        {description && (
          <span className="block text-xs text-ink-muted mt-0.5 leading-relaxed">{description}</span>
        )}
      </div>
    </label>
  );
}

// ---------------------------------------------------------------------------
// SortableLink sub-component
// ---------------------------------------------------------------------------

interface SortableLinkProps {
  link: CustomLink;
  onUpdate: (id: string, field: keyof CustomLink, value: string) => void;
  onRemove: (id: string) => void;
  disabled?: boolean;
}

function SortableLink({ link, onUpdate, onRemove, disabled }: SortableLinkProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: link.id, ...(disabled !== undefined ? { disabled } : {}) });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
  };

  const isImgIcon = link.icon.startsWith('http') || link.icon.startsWith('data:') || link.icon.startsWith('/');
  const [showImgInput, setShowImgInput] = React.useState(isImgIcon);
  const CurrentIcon = isImgIcon ? null : (ICON_COMPONENT[link.icon] ?? LinkIcon);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`rounded-xl bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 p-3 space-y-3 transition-all
                  ${isDragging ? 'opacity-60 shadow-2xl scale-[0.98] border-accent-500/30' : 'hover:border-line-strong'}`}
    >
      {/* Header row: grip + icon picker + remove */}
      <div className="flex items-center gap-2">
        {!disabled && (
          <button
            type="button"
            {...attributes}
            {...listeners}
            className="p-1 text-ink-faint hover:text-ink cursor-grab active:cursor-grabbing touch-none transition-colors"
          >
            <GripVertical className="w-4 h-4" />
          </button>
        )}
        <div className="flex flex-wrap gap-1 flex-1">
          {LINK_ICONS.map(({ key, Icon }) => (
            <button
              key={key}
              type="button"
              disabled={disabled}
              onClick={() => { onUpdate(link.id, 'icon', key); setShowImgInput(false); }}
              title={key}
              className={`p-1.5 rounded-lg transition-all ${
                link.icon === key && !isImgIcon
                  ? 'bg-accent-600 text-white shadow-sm'
                  : 'bg-slate-200 dark:bg-white/5 text-ink-faint hover:bg-slate-300 dark:hover:bg-white/10 hover:text-ink'
              } disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              <Icon className="w-3.5 h-3.5" />
            </button>
          ))}
          {/* Custom image icon button */}
          <button
            type="button"
            disabled={disabled}
            onClick={() => { setShowImgInput((v) => !v); if (!showImgInput) onUpdate(link.id, 'icon', ''); }}
            title="Custom image"
            className={`p-1.5 rounded-lg transition-all text-xs font-bold ${
              isImgIcon
                ? 'bg-accent-600 text-white shadow-sm'
                : 'bg-slate-200 dark:bg-white/5 text-ink-faint hover:bg-slate-300 dark:hover:bg-white/10 hover:text-ink'
            } disabled:opacity-40 disabled:cursor-not-allowed`}
          >
            IMG
          </button>
        </div>
        {!disabled && (
          <button
            type="button"
            onClick={() => onRemove(link.id)}
            className="flex-shrink-0 p-1.5 rounded-lg text-ink-faint hover:text-danger-400 hover:bg-danger-500/10 transition-all"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Custom image URL input */}
      {showImgInput && !disabled && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-panel border border-accent-500/20">
          {isImgIcon ? (
            <img src={link.icon} alt="" className="w-4 h-4 object-contain rounded flex-shrink-0" />
          ) : (
            <Globe className="w-3.5 h-3.5 text-ink-faint flex-shrink-0" />
          )}
          <input
            type="url"
            value={isImgIcon ? link.icon : ''}
            onChange={(e) => onUpdate(link.id, 'icon', e.target.value)}
            placeholder="https://... (image URL for icon)"
            maxLength={500}
            className="w-full bg-transparent text-xs text-ink placeholder:text-ink-faint focus:outline-none"
          />
        </div>
      )}

      {/* Inputs row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-panel border border-slate-200 dark:border-white/[0.08]">
          {isImgIcon
            ? <img src={link.icon} alt="" className="w-3.5 h-3.5 object-contain rounded flex-shrink-0" />
            : CurrentIcon
              ? <CurrentIcon className="w-3.5 h-3.5 text-ink-faint flex-shrink-0" />
              : <LinkIcon className="w-3.5 h-3.5 text-ink-faint flex-shrink-0" />
          }
          <input
            type="text"
            value={link.title}
            onChange={(e) => onUpdate(link.id, 'title', e.target.value)}
            placeholder="Label"
            disabled={disabled}
            maxLength={200}
            className="w-full bg-transparent text-sm text-ink placeholder:text-ink-faint focus:outline-none"
          />
        </div>
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-panel border border-slate-200 dark:border-white/[0.08]">
          <Globe className="w-3.5 h-3.5 text-ink-faint flex-shrink-0" />
          <input
            type="url"
            value={link.url}
            onChange={(e) => onUpdate(link.id, 'url', e.target.value)}
            placeholder="https://..."
            disabled={disabled}
            maxLength={500}
            className="w-full bg-transparent text-sm text-ink placeholder:text-ink-faint focus:outline-none"
          />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main CardEditor component
// ---------------------------------------------------------------------------

export default function CardEditor() {
  const { t } = useAppTranslation();
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const isNew = !slug || slug === 'new';
  const queryUserId = searchParams.get('userId');

  const [activeTab, setActiveTab]       = useState<TabId>('details');
  const [data, setData]                 = useState<CardData>(getDefaultTemplate());
  const [orgSettings, setOrgSettings]   = useState<OrgSettings>({});
  const [appSettings, setAppSettings]   = useState<AppSettings>({});
  const [targetUserId, setTargetUserId] = useState<string | null>(null);
  const [loading, setLoading]           = useState(!isNew);
  const [saving, setSaving]             = useState(false);
  const [saveSuccess, setSaveSuccess]   = useState(false);
  const [error, setError]               = useState<string | null>(null);
  // Preview dark mode is isolated from the dashboard theme — toggling here
  // only affects the phone mock, not the surrounding dashboard UI.
  const [previewDark, setPreviewDark] = useState(true);
  const togglePreviewDark = () => setPreviewDark((v) => !v);
  const [showPreview, setShowPreview]   = useState(false);

  // User picker (admin/owner/superadmin creating a card for someone else)
  const canPickUser = isNew && ['admin', 'owner', 'superadmin'].includes(user?.role ?? '');
  const [userList, setUserList]       = useState<PickableUser[]>([]);
  const [userSearch, setUserSearch]   = useState('');
  const [userPickerOpen, setUserPickerOpen] = useState(false);

  const links: CustomLink[] = (data.links ?? []) as CustomLink[];

  // ── Load ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetches: Promise<unknown>[] = [
      fetch('/api/settings', { credentials: 'include' })
        .then((r) => r.ok ? r.json() : null)
        .then((d) => d && setAppSettings(d as AppSettings))
        .catch(() => null),
    ];

    if (!isNew) {
      const url = `/api/admin/cards/${slug}${queryUserId ? `?userId=${queryUserId}` : ''}`;
      fetches.push(
        fetch(url, { credentials: 'include' })
          .then((r) => r.json())
          .then((d: { data?: CardData; orgSettings?: OrgSettings; userId?: string }) => {
            if (d.data) setData(mergeCardDataWithDefaults(d.data, d.orgSettings as AppSettings));
            if (d.orgSettings) setOrgSettings(d.orgSettings);
            if (d.userId) setTargetUserId(d.userId);
          })
          .catch(() => setError(t('cardEditor.loadFailed')))
      );
    } else {
      fetches.push(
        fetch('/api/admin/settings', { credentials: 'include' })
          .then((r) => r.ok ? r.json() : null)
          .then((d) => {
            if (d) {
              const os = d as OrgSettings;
              setOrgSettings(os);
              const baseData = mergeCardDataWithDefaults({}, d as AppSettings);
              // Pre-fill email from current user
              if (user?.email) {
                baseData.contact = { ...baseData.contact, email: user.email };
              }
              // Pre-fill company from org default_organisation
              if (os.default_organisation) {
                baseData.personal = { ...baseData.personal, company: os.default_organisation };
              }
              // Apply org default card color
              if (os.default_card_color) {
                baseData.theme = { ...baseData.theme, color: os.default_card_color };
              }
              setData(baseData);
            } else if (user?.email) {
              // No org settings but still pre-fill email
              setData((d) => ({ ...d, contact: { ...d.contact, email: user.email! } }));
            }
          })
          .catch(() => null)
      );
    }

    Promise.all(fetches).finally(() => setLoading(false));
  }, [slug, isNew, queryUserId]);

  // ── Fetch user list for picker (admin/owner/superadmin only, new cards) ───
  useEffect(() => {
    if (!canPickUser) return;
    fetch('/api/admin/users', { credentials: 'include' })
      .then((r) => r.ok ? r.json() : null)
      .then((d: { users?: PickableUser[] } | null) => {
        if (d?.users) setUserList(d.users);
      })
      .catch(() => null);
  }, [canPickUser]);

  const filteredUsers = useMemo(() =>
    userList.filter((u) => {
      const q = userSearch.toLowerCase();
      return !q || u.email.toLowerCase().includes(q) || u.firstName.toLowerCase().includes(q) || u.lastName.toLowerCase().includes(q);
    }).slice(0, 20),
  [userList, userSearch]);

  // ── State helpers ─────────────────────────────────────────────────────────
  const updP = useCallback((field: string, value: string) =>
    setData((d) => ({ ...d, personal: { ...d.personal, [field]: value } })), []);
  const updC = useCallback((field: string, value: string | boolean) =>
    setData((d) => ({ ...d, contact: { ...d.contact, [field]: value } })), []);
  const updS = useCallback((key: string, value: string) =>
    setData((d) => ({ ...d, social: { ...d.social, [key]: value } })), []);
  const updTheme = useCallback((key: string, value: string) =>
    setData((d) => ({ ...d, theme: { ...d.theme, [key]: value } })), []);
  const updImg = useCallback((key: string, value: string | null) =>
    setData((d) => ({ ...d, images: { ...d.images, [key]: value } })), []);
  const updPrivacy = useCallback((key: string, value: boolean) =>
    setData((d) => ({ ...d, privacy: { ...d.privacy, [key]: value } })), []);

  // ── Links helpers ─────────────────────────────────────────────────────────
  function addLink() {
    setData((d) => ({
      ...d,
      links: [...(d.links ?? []), { id: `${Date.now()}`, title: '', url: '', icon: 'link' }],
    }));
  }
  function updateLink(id: string, field: keyof CustomLink, value: string) {
    setData((d) => ({
      ...d,
      links: (d.links ?? []).map((l) => (l.id === id ? { ...l, [field]: value } : l)) as CustomLink[],
    }));
  }
  function removeLink(id: string) {
    setData((d) => ({ ...d, links: (d.links ?? []).filter((l) => l.id !== id) }));
  }

  // ── DnD ───────────────────────────────────────────────────────────────────
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oi = links.findIndex((l) => l.id === active.id);
      const ni = links.findIndex((l) => l.id === over.id);
      setData((d) => ({ ...d, links: arrayMove(links, oi, ni) }));
    }
  }

  // ── Image upload ──────────────────────────────────────────────────────────
  async function handleImageUpload(type: 'avatar' | 'banner', e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await fetch(`/api/upload/${type}`, { method: 'POST', credentials: 'include', body: fd });
      const result = await res.json() as { url?: string; error?: string };
      result.url ? updImg(type, result.url) : setError(result.error ?? t('cardEditor.uploadFailed'));
    } catch {
      setError(t('cardEditor.imageUploadFailed'));
    }
  }

  // ── Save ──────────────────────────────────────────────────────────────────
  async function handleSave() {
    setError(null);
    setSaving(true);
    setSaveSuccess(false);

    const firstName = (data.personal?.firstName ?? '').trim();
    const lastName  = (data.personal?.lastName  ?? '').trim();

    let saveSlug = isNew
      ? `${firstName}-${lastName}`.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').substring(0, 78) || `card-${Date.now()}`
      : (slug ?? '');
    if (!saveSlug || saveSlug.length < 1) saveSlug = `card-${Date.now()}`;

    const userId = targetUserId ?? (queryUserId || user?.id);

    try {
      const res = await fetch(`/api/cards/${saveSlug}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...data, userId }),
      });
      const result = await res.json() as { success?: boolean; error?: string };
      if (!res.ok) throw new Error(result.error ?? t('cardEditor.saveError'));
      setSaveSuccess(true);
      setTimeout(() => navigate('/cards'), 1200);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('cardEditor.saveException'));
    } finally {
      setSaving(false);
    }
  }

  // ── Loading state ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Layout title={isNew ? t('cardEditor.newCard') : t('cardEditor.editCard')}>
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <div className="w-10 h-10 rounded-full bg-accent-600/20 border border-accent-500/20 flex items-center justify-center">
            <RefreshCw className="w-5 h-5 animate-spin text-accent-400" />
          </div>
          <p className="text-sm text-ink-faint">{t('cardEditor.loading')}</p>
        </div>
      </Layout>
    );
  }

  const canEditImages  = orgSettings.allow_image_customisation  !== false;
  const canEditLinks   = orgSettings.allow_links_customisation  !== false;
  const canEditTheme   = orgSettings.allow_theme_customisation  !== false;
  const canEditPrivacy = orgSettings.allow_privacy_customisation !== false;
  const lockedCompany  = Boolean(orgSettings.default_organisation);

  const p    = data.personal ?? {};
  const c    = data.contact  ?? {};
  const s    = data.social   ?? {};
  const th   = data.theme    ?? {};
  const img  = data.images   ?? {};
  const priv = data.privacy  ?? {};

  // ── Save button shared ────────────────────────────────────────────────────
  const SaveBtn = ({ full }: { full?: boolean }) => (
    <button
      onClick={handleSave}
      disabled={saving || saveSuccess}
      className={`flex items-center justify-center gap-2 rounded-full font-semibold text-white transition-all shadow-lg
                  ${full ? 'w-full py-3 text-sm' : 'px-5 py-2 text-sm'}
                  ${saveSuccess
                    ? 'bg-success-600 shadow-success-500/20'
                    : saving
                    ? 'bg-accent-700/50 cursor-wait'
                    : 'bg-accent-600 hover:opacity-90 shadow-accent-500/20'}`}
    >
      {saveSuccess ? (
        <><Check className="w-4 h-4" /> {t('settings.saved')}</>
      ) : saving ? (
        <><RefreshCw className="w-4 h-4 animate-spin" /> {t('settings.saving')}</>
      ) : (
        t('common.save')
      )}
    </button>
  );

  return (
    <Layout title={isNew ? t('cardEditor.newCard') : t('cardEditor.editCard')}>

      {/* ── Page header ───────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-6 gap-4">
        <button
          onClick={() => navigate('/cards')}
          className="flex items-center gap-2 text-sm text-ink-muted hover:text-ink transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="hidden sm:inline">{t('cardEditor.backToCards')}</span>
          <span className="sm:hidden">{t('common.back')}</span>
        </button>

        <div className="flex items-center gap-3">
          {/* Mobile: preview toggle */}
          <button
            type="button"
            onClick={() => setShowPreview((v) => !v)}
            className="lg:hidden flex items-center gap-1.5 rounded-full border border-line px-3 py-2 text-xs text-ink-muted hover:text-ink hover:border-line-strong transition-all"
          >
            <Eye className="w-3.5 h-3.5" />
            {t('common.preview')}
          </button>

          {error && (
            <p className="hidden sm:block text-xs text-danger-400 max-w-[200px] truncate">{error}</p>
          )}

          {/* Desktop save */}
          <div className="hidden sm:block">
            <SaveBtn />
          </div>
        </div>
      </div>

      {/* Error on mobile (below header) */}
      {error && <p className="sm:hidden text-xs text-danger-400 mb-4 px-1">{error}</p>}

      {/* ── User picker (admin/owner/superadmin — new card only) ──────────── */}
      {canPickUser && (
        <div className="mb-5 rounded-2xl bg-panel border border-line px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
          <span className="text-xs font-semibold text-ink-muted flex-shrink-0">Karte erstellen für:</span>
          <div className="relative flex-1">
            <button
              type="button"
              onClick={() => setUserPickerOpen((o) => !o)}
              className="w-full flex items-center justify-between rounded-xl bg-panel border border-line px-3 py-2 text-sm text-ink hover:border-line-strong transition-colors text-left"
            >
              {targetUserId
                ? (() => {
                    const u = userList.find((u) => u.id === targetUserId);
                    return u ? `${u.firstName} ${u.lastName} (${u.email})` : 'Ausgewählt';
                  })()
                : user
                  ? `Ich selbst (${user.email})`
                  : 'Für mich selbst'}
              <span className="text-ink-faint ml-2">▾</span>
            </button>
            {userPickerOpen && (
              <div className="absolute top-full left-0 right-0 mt-1 z-50 rounded-xl bg-panel border border-line shadow-2xl overflow-hidden">
                <div className="p-2 border-b border-line">
                  <input
                    autoFocus
                    type="text"
                    placeholder="Suchen…"
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    className="w-full rounded-lg bg-panel border border-line px-3 py-1.5 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:border-accent-500/50"
                  />
                </div>
                <div className="max-h-56 overflow-y-auto">
                  {/* Myself option */}
                  <button
                    type="button"
                    onClick={() => { setTargetUserId(null); setUserPickerOpen(false); setUserSearch(''); }}
                    className={`w-full px-3 py-2.5 text-left text-sm hover:bg-raised transition-colors flex items-center gap-2 ${!targetUserId ? 'text-accent-600 dark:text-accent-300' : 'text-slate-600 dark:text-white/70'}`}
                  >
                    <span className="w-6 h-6 rounded-full bg-accent-100 dark:bg-accent-600/30 flex items-center justify-center text-xs font-bold text-accent-700 dark:text-accent-300 flex-shrink-0">
                      {user?.email?.charAt(0).toUpperCase()}
                    </span>
                    <span>Ich selbst ({user?.email})</span>
                  </button>
                  {filteredUsers.map((u) => (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => {
                        setTargetUserId(u.id);
                        setUserPickerOpen(false);
                        setUserSearch('');
                        // Pre-fill name + email
                        setData((d) => ({
                          ...d,
                          personal: { ...d.personal, ...(u.firstName && { firstName: u.firstName }), ...(u.lastName && { lastName: u.lastName }) },
                          contact: { ...d.contact, email: u.email },
                        }));
                      }}
                      className={`w-full px-3 py-2.5 text-left text-sm hover:bg-raised transition-colors flex items-center gap-2 ${targetUserId === u.id ? 'text-accent-600 dark:text-accent-300' : 'text-slate-600 dark:text-white/70'}`}
                    >
                      <span className="w-6 h-6 rounded-full bg-raised flex items-center justify-center text-xs font-bold text-ink-muted flex-shrink-0">
                        {u.email.charAt(0).toUpperCase()}
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate">{u.firstName} {u.lastName}</span>
                        <span className="block text-xs text-ink-faint truncate">{u.email}</span>
                      </span>
                    </button>
                  ))}
                  {filteredUsers.length === 0 && userSearch && (
                    <p className="px-3 py-3 text-sm text-ink-faint text-center">Keine Ergebnisse</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Mobile preview overlay ─────────────────────────────────────────── */}
      {showPreview && (
        <div className="lg:hidden fixed inset-0 z-50 bg-canvas/95 backdrop-blur-sm flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-line">
            <span className="text-sm font-semibold text-ink">{t('common.preview')}</span>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => togglePreviewDark()}
                className="flex items-center gap-1.5 text-xs text-ink-muted hover:text-ink transition-colors"
              >
                {previewDark ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
                {previewDark ? t('cardEditor.lightMode') : t('cardEditor.darkMode')}
              </button>
              <button
                type="button"
                onClick={() => setShowPreview(false)}
                className="p-1.5 rounded-lg text-ink-muted hover:text-ink hover:bg-raised transition-all"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto flex justify-center pt-6 px-4 pb-24">
            <div className="w-full max-w-sm mx-auto">
              <PhoneMock darkMode={previewDark}>
                <CardDisplay
                  data={data}
                  settings={appSettings}
                  darkMode={previewDark}
                  toggleDarkMode={() => togglePreviewDark()}
                />
              </PhoneMock>
            </div>
          </div>
          <div className="p-4 bg-canvas border-t border-line">
            <SaveBtn full />
          </div>
        </div>
      )}

      {/* ── Two-column layout ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-start">

        {/* ── Left panel: Tabs + Form ──────────────────────────────────────── */}
        <div className="lg:col-span-3 space-y-4">

          {/* Tab bar */}
          <div className="flex gap-1 rounded-xl bg-panel border border-line p-1 overflow-x-auto">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`flex-shrink-0 flex-1 min-w-fit whitespace-nowrap rounded-lg px-3 py-2 text-xs font-semibold transition-all ${
                  activeTab === tab.id
                    ? 'bg-accent-600 text-white shadow-sm'
                    : 'text-ink-faint hover:text-ink hover:bg-raised'
                }`}
              >
                {t(`cardEditor.tabs.${tab.id}`, tab.label)}
              </button>
            ))}
          </div>

          {/* ── Tab: Details ────────────────────────────────────────────────── */}
          {activeTab === 'details' && (
            <div className="space-y-4">

              <FormSection title={t('cardEditor.sections.person')}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label={t('users.firstName')} icon={User} value={p.firstName ?? ''} onChange={(v) => updP('firstName', v)} placeholder={t('cardEditor.placeholders.firstName')} autoComplete="given-name" />
                  <Field label={t('users.lastName')} icon={User} value={p.lastName ?? ''} onChange={(v) => updP('lastName', v)} placeholder={t('cardEditor.placeholders.lastName')} autoComplete="family-name" />
                  <Field label={t('common.fields.jobTitle')} icon={Briefcase} value={p.title ?? ''} onChange={(v) => updP('title', v)} placeholder={t('cardEditor.placeholders.jobTitle')} autoComplete="organization-title" />
                  {lockedCompany ? (
                    <LockedOption message={t('cardEditor.lockedByOrganisation')}>
                      <Field label={t('cardEditor.fields.company')} value={p.company ?? ''} onChange={() => {}} disabled autoComplete="organization" />
                    </LockedOption>
                  ) : (
                    <Field label={t('cardEditor.fields.company')} value={p.company ?? ''} onChange={(v) => updP('company', v)} placeholder={t('cardEditor.placeholders.company')} autoComplete="organization" />
                  )}
                  <div className="sm:col-span-2">
                    <Field label={t('bulkImport.fields.location')} icon={MapPin} value={p.location ?? ''} onChange={(v) => updP('location', v)} placeholder={t('cardEditor.placeholders.location')} />
                  </div>
                  <div className="sm:col-span-2">
                    <FieldArea label={t('bulkImport.fields.bio')} value={p.bio ?? ''} onChange={(v) => updP('bio', v)} rows={4} placeholder={t('cardEditor.placeholders.bio')} />
                  </div>
                </div>
              </FormSection>

              <FormSection title={t('cardEditor.sections.contact')}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label={t('common.email')} icon={Mail} type="email" name="email" value={c.email ?? ''} onChange={(v) => updC('email', v)} placeholder={t('cardEditor.placeholders.email')} autoComplete="email" />
                  <Field label={t('bulkImport.fields.website')} icon={Globe} type="url" name="url" value={c.website ?? ''} onChange={(v) => updC('website', v)} placeholder={t('cardEditor.placeholders.website')} autoComplete="url" />
                  <Field label={t('bulkImport.fields.phone1')} icon={Phone} name="tel" value={c.phone ?? ''} onChange={(v) => updC('phone', v)} placeholder={t('cardEditor.placeholders.phone1')} autoComplete="tel" />
                  <Field label={t('cardEditor.fields.phoneLabel1')} name="phone_label_1" value={c.phoneLabel ?? t('common.phoneLabels.mobile')} onChange={(v) => updC('phoneLabel', v)} placeholder={t('common.phoneLabels.mobile')} autoComplete="new-password" />
                  <Field label={t('bulkImport.fields.phone2')} icon={Phone} name="tel2" value={(c as Record<string, unknown>)['phoneSecondary'] as string ?? ''} onChange={(v) => updC('phoneSecondary', v)} placeholder={t('cardEditor.placeholders.phone2')} autoComplete="tel-national" />
                  <Field label={t('cardEditor.fields.phoneLabel2')} name="phone_label_2" value={(c as Record<string, unknown>)['phoneSecondaryLabel'] as string ?? t('common.phoneLabels.phone')} onChange={(v) => updC('phoneSecondaryLabel', v)} placeholder={t('common.phoneLabels.phone')} autoComplete="new-password" />
                </div>
              </FormSection>

              <FormSection title={t('cardEditor.sections.address')}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <Field label={t('cardEditor.fields.streetAddress')} icon={MapPin} name="street-address" value={c.addressStreet ?? ''} onChange={(v) => updC('addressStreet', v)} placeholder={t('cardEditor.placeholders.streetAddress')} autoComplete="street-address" />
                  </div>
                  <Field label={t('bulkImport.fields.postalCode')} name="postal-code" value={c.addressPostalCode ?? ''} onChange={(v) => updC('addressPostalCode', v)} placeholder={t('cardEditor.placeholders.postalCode')} autoComplete="postal-code" />
                  <Field label={t('bulkImport.fields.city')} name="address-level2" value={c.addressCity ?? ''} onChange={(v) => updC('addressCity', v)} placeholder={t('cardEditor.placeholders.city')} autoComplete="address-level2" />
                  <div className="sm:col-span-2">
                    <FieldSelect label={t('bulkImport.fields.country')} value={c.addressCountry ?? 'DE'} onChange={(v) => updC('addressCountry', v)} options={COUNTRIES.map((country) => ({ ...country, label: t(`common.countries.${country.value}`, country.label) }))} />
                  </div>
                  <div className="sm:col-span-2">
                    <FieldToggle
                      label={t('cardEditor.fields.showRouteButton')}
                      description={t('cardEditor.fields.showRouteButtonDescription')}
                      checked={Boolean((c as Record<string, unknown>)['showRouteButton'])}
                      onChange={(v) => updC('showRouteButton', v)}
                    />
                  </div>
                </div>
              </FormSection>

              <FormSection title={t('cardEditor.sections.social')}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="LinkedIn" icon={Linkedin} type="url" value={s['linkedin'] ?? ''} onChange={(v) => updS('linkedin', v)} placeholder="https://linkedin.com/in/..." />
                  <Field label="Twitter / X" icon={Twitter} type="url" value={s['twitter'] ?? ''} onChange={(v) => updS('twitter', v)} placeholder="https://x.com/..." />
                  <Field label="Instagram" icon={Instagram} type="url" value={s['instagram'] ?? ''} onChange={(v) => updS('instagram', v)} placeholder="https://instagram.com/..." />
                  <Field label="GitHub" icon={Github} type="url" value={s['github'] ?? ''} onChange={(v) => updS('github', v)} placeholder="https://github.com/..." />
                </div>
              </FormSection>
            </div>
          )}

          {/* ── Tab: Links ──────────────────────────────────────────────────── */}
          {activeTab === 'links' && (
            <FormSection
              title={t('cardEditor.sections.customLinks')}
              description={t('cardEditor.sections.customLinksDescription')}
              actions={
                canEditLinks ? (
                  <button
                    type="button"
                    onClick={addLink}
                    disabled={links.length >= 20}
                    className="flex items-center gap-1.5 rounded-full bg-gradient-to-r from-accent-600 to-accent-600
                               px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90
                               disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
                  >
                    <Plus className="w-3.5 h-3.5" /> {t('cardEditor.actions.addLink')}
                  </button>
                ) : undefined
              }
            >
              {!canEditLinks ? (
                <LockedOption message={t('cardEditor.lockedLinks')}>
                  <div className="h-16 rounded-xl bg-panel border border-line" />
                </LockedOption>
              ) : links.length === 0 ? (
                <div className="rounded-xl border border-dashed border-line p-10 text-center">
                  <LinkIcon className="w-8 h-8 text-ink-faint mx-auto mb-3" />
                  <p className="text-sm text-ink-faint">{t('cardEditor.emptyLinks')}</p>
                  <p className="text-xs text-ink-faint mt-1">{t('cardEditor.emptyLinksHint')}</p>
                </div>
              ) : (

                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={links.map((l) => l.id)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-2">
                      {links.map((link) => (
                        <SortableLinkComponent key={link.id} link={link} onUpdate={updateLink} onRemove={removeLink} />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </FormSection>
          )}

          {/* ── Tab: Bilder ─────────────────────────────────────────────────── */}
          {activeTab === 'images' && (
            <FormSection title={t('cardEditor.tabs.images')} description={t('cardEditor.sections.imagesDescription')}>
              {!canEditImages ? (
                <LockedOption message={t('cardEditor.lockedImages')}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="h-32 rounded-xl bg-panel border border-line" />
                    <div className="h-32 rounded-xl bg-panel border border-line" />
                  </div>
                </LockedOption>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <ImageUpload
                    label={t('cardEditor.fields.profileImage')}
                    {...(img.avatar ? { image: img.avatar } : {})}
                    onUpload={(e) => handleImageUpload('avatar', e)}
                    onRemove={() => updImg('avatar', null)}
                  />
                  <ImageUpload
                    label={t('cardEditor.fields.headerBanner')}
                    isBanner
                    {...(img.banner ? { image: img.banner } : {})}
                    onUpload={(e) => handleImageUpload('banner', e)}
                    onRemove={() => updImg('banner', null)}
                  />
                </div>
              )}
            </FormSection>
          )}

          {/* ── Tab: Style ──────────────────────────────────────────────────── */}
          {activeTab === 'style' && (
            <FormSection title={t('cardEditor.sections.colorScheme')} description={t('cardEditor.sections.colorSchemeDescription')}>
              {!canEditTheme ? (
                <LockedOption message={t('cardEditor.lockedStyle')}>
                  <ColorSelector selectedColor={th.color ?? 'indigo'} onSelect={() => {}} />
                </LockedOption>
              ) : (
                <>
                  <ColorSelector
                    selectedColor={th.customAccent ? '' : (th.color ?? 'indigo')}
                    onSelect={(color) => { updTheme('color', color); updTheme('customAccent', ''); }}
                  />
                  <div className="mt-6 grid gap-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="card-accent" className="block text-xs font-semibold uppercase tracking-wide text-ink-muted mb-1">{t('cardEditor.design.customAccent', 'Eigene Akzentfarbe')}</label>
                      <div className="flex items-center gap-3">
                        <input
                          id="card-accent"
                          type="color"
                          value={th.customAccent || '#6366f1'}
                          onChange={(e) => updTheme('customAccent', e.target.value)}
                          className="h-9 w-12 cursor-pointer rounded border border-line bg-transparent"
                        />
                        {th.customAccent
                          ? <button type="button" onClick={() => updTheme('customAccent', '')} className="text-xs text-ink-muted hover:text-ink">{t('cardEditor.design.reset', 'Zurücksetzen')}</button>
                          : <span className="text-xs text-ink-faint">{t('cardEditor.design.usesPreset', 'Nutzt die Farbvorlage')}</span>}
                      </div>
                    </div>
                    <div>
                      <label htmlFor="card-font" className="block text-xs font-semibold uppercase tracking-wide text-ink-muted mb-1">{t('cardEditor.design.font', 'Schriftart')}</label>
                      <select
                        id="card-font"
                        value={th.font || 'default'}
                        onChange={(e) => updTheme('font', e.target.value === 'default' ? '' : e.target.value)}
                        className="w-full rounded-lg bg-panel border border-line px-3 py-2 text-sm text-ink focus:outline-none focus:border-accent-500/50"
                      >
                        {Object.entries(CARD_FONTS).map(([k, v]) => <option key={k} value={k}>{t('cardEditor.design.font_' + k, v.label)}</option>)}
                      </select>
                    </div>
                  </div>
                </>
              )}
            </FormSection>
          )}

          {/* ── Tab: Privatsphäre ───────────────────────────────────────────── */}
          {activeTab === 'privacy' && (
            <FormSection title={t('cardEditor.tabs.privacy')} description={t('cardEditor.sections.privacyDescription')}>
              {!canEditPrivacy ? (
                <LockedOption message={t('cardEditor.lockedPrivacy')}>
                  <div className="space-y-5 opacity-50 pointer-events-none">
                    <FieldToggle label={t('cardEditor.privacy.requireInteraction')} checked={priv.requireInteraction ?? false} onChange={() => {}} />
                    <FieldToggle label={t('cardEditor.privacy.obfuscate')} checked={priv.clientSideObfuscation ?? true} onChange={() => {}} />
                    <FieldToggle label={t('cardEditor.privacy.blockRobots')} checked={priv.blockRobots ?? true} onChange={() => {}} />
                  </div>
                </LockedOption>
              ) : (
                <div className="space-y-6">
                  <FieldToggle
                    label={t('cardEditor.privacy.requireInteraction')}
                    description={t('cardEditor.privacy.requireInteractionDescription')}
                    checked={priv.requireInteraction ?? false}
                    onChange={(v) => updPrivacy('requireInteraction', v)}
                  />
                  <div className="h-px bg-slate-200 dark:bg-white/5" />
                  <FieldToggle
                    label={t('cardEditor.privacy.obfuscate')}
                    description={t('cardEditor.privacy.obfuscateDescription')}
                    checked={priv.clientSideObfuscation ?? true}
                    onChange={(v) => updPrivacy('clientSideObfuscation', v)}
                  />
                  <div className="h-px bg-slate-200 dark:bg-white/5" />
                  <FieldToggle
                    label={t('cardEditor.privacy.blockRobots')}
                    description={t('cardEditor.privacy.blockRobotsDescription')}
                    checked={priv.blockRobots ?? true}
                    onChange={(v) => updPrivacy('blockRobots', v)}
                  />
                </div>
              )}
            </FormSection>
          )}
        </div>

        {/* ── Right panel: Live preview ────────────────────────────────────── */}
        <div className="hidden lg:flex lg:col-span-2 flex-col sticky top-20 gap-4">
          {/* Label */}
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-widest text-ink-faint">{t('common.preview')}</span>
            <button
              type="button"
              onClick={() => togglePreviewDark()}
              className="flex items-center gap-1.5 rounded-full border border-slate-200 dark:border-white/10 px-3 py-1.5 text-xs text-slate-400 dark:text-white/35
                         hover:text-slate-600 dark:hover:text-white/60 hover:border-slate-300 dark:hover:border-white/20 transition-all"
            >
              {previewDark ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
              {previewDark ? t('cardEditor.lightMode') : t('cardEditor.darkMode')}
            </button>
          </div>

          {/* Phone mock */}
          <PhoneMock darkMode={previewDark}>
            <CardDisplay
              data={data}
              settings={appSettings}
              darkMode={previewDark}
              toggleDarkMode={() => togglePreviewDark()}
            />
          </PhoneMock>
        </div>
      </div>

      {/* ── Mobile fixed save bar ─────────────────────────────────────────── */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 p-4 bg-canvas/90 backdrop-blur-sm border-t border-white/[0.06] z-40">
        {error && <p className="text-xs text-danger-400 mb-2 text-center">{error}</p>}
        <SaveBtn full />
      </div>
      <div className="sm:hidden h-20" />
    </Layout>
  );
}

// ---------------------------------------------------------------------------
// PhoneMock helper
// ---------------------------------------------------------------------------

function PhoneMock({ children, darkMode }: { children: React.ReactNode; darkMode?: boolean }) {
  return <PhoneMockComponent darkMode={darkMode}>{children}</PhoneMockComponent>;
}
